/**
 * JSON helpers: preserve precision for integers beyond `Number.MAX_SAFE_INTEGER` on parse
 * and emit such integers as JSON number tokens (not stringified `BigInt`) on stringify.
 * @module json-with-bigint
 */

const MAX_SAFE_BIGINT = BigInt(Number.MAX_SAFE_INTEGER);
const MIN_SAFE_BIGINT = BigInt(Number.MIN_SAFE_INTEGER);

/** Marks values that were JSON *number* literals rewritten as strings; real JSON strings never use this prefix. */
const BIGINT_SENTINEL_PREFIX = '__BIGINT__';

const isDigit = (ch) => ch >= '0' && ch <= '9';

function isFractionOrExponent(char) {
  return char === '.' || char === 'e' || char === 'E';
}

function shouldQuoteIntegerAsBigInt(intPart) {
  if (!/^-?\d{16,}$/.test(intPart)) {
    return false;
  }
  try {
    const bi = BigInt(intPart);
    return bi > MAX_SAFE_BIGINT || bi < MIN_SAFE_BIGINT;
  } catch (error) {
    if (error instanceof Error) {
      return false;
    }
    throw error;
  }
}

function advanceOverLeadingMinusSign(s, i, end) {
  return i < end && s[i] === '-' ? i + 1 : i;
}

function advanceOverDigits(s, i, end) {
  let j = i;
  while (j < end && isDigit(s[j])) {
    j += 1;
  }
  return j;
}

function advanceOverFractionIfPresent(s, i, end) {
  if (i >= end || s[i] !== '.') {
    return i;
  }
  return advanceOverDigits(s, i + 1, end);
}

function advanceOverExponentIfPresent(s, i, end) {
  if (i >= end || (s[i] !== 'e' && s[i] !== 'E')) {
    return i;
  }
  let j = i + 1;
  if (j < end && (s[j] === '+' || s[j] === '-')) {
    j += 1;
  }
  return advanceOverDigits(s, j, end);
}

/**
 * Read one JSON decimal or scientific number literal starting at `startIndex`.
 */
function extractDecimalOrScientificNumber(s, startIndex, endIndex) {
  const tokenStart = startIndex;
  let i = advanceOverLeadingMinusSign(s, startIndex, endIndex);
  i = advanceOverDigits(s, i, endIndex);
  i = advanceOverFractionIfPresent(s, i, endIndex);
  i = advanceOverExponentIfPresent(s, i, endIndex);
  return { endIndex: i, extractedNumber: s.slice(tokenStart, i) };
}

function scanQuotedStringContent(s, n, i, out, isInsideJsonString, isReadingEscapedChar) {
  if (isReadingEscapedChar) {
    out.push(s[i++]);
    return { i, isInsideJsonString, isReadingEscapedChar: false };
  }
  const char = s[i++];
  out.push(char);
  if (char === '\\') {
    return { i, isInsideJsonString, isReadingEscapedChar: true };
  }
  if (char === '"') {
    return { i, isInsideJsonString: false, isReadingEscapedChar: false };
  }
  return { i, isInsideJsonString, isReadingEscapedChar: false };
}

function appendJsonNumericToken(s, n, i, out) {
  const start = i;
  const char = s[i];
  if (char === '-') {
    i += 1;
    if (i >= n || !isDigit(s[i])) {
      out.push('-');
      return i;
    }
  }
  const integerStartingIndex = start;
  i = advanceOverDigits(s, i, n);
  const integerAsString = s.slice(integerStartingIndex, i);
  if (i < n && isFractionOrExponent(s[i])) {
    const { endIndex, extractedNumber } = extractDecimalOrScientificNumber(s, start, n);
    out.push(extractedNumber);
    return endIndex;
  }
  if (shouldQuoteIntegerAsBigInt(integerAsString)) {
    out.push(`"${BIGINT_SENTINEL_PREFIX}${integerAsString}"`);
  } else {
    out.push(integerAsString);
  }
  return i;
}

function rewriteUnsafeJsonIntegerLiterals(s) {
  const out = [];
  let i = 0;
  const n = s.length;
  let isInsideJsonString = false;
  let isReadingEscapedChar = false;

  while (i < n) {
    if (isInsideJsonString || isReadingEscapedChar) {
      const st = scanQuotedStringContent(s, n, i, out, isInsideJsonString, isReadingEscapedChar);
      i = st.i;
      isInsideJsonString = st.isInsideJsonString;
      isReadingEscapedChar = st.isReadingEscapedChar;
    } else {
      const char = s[i];
      if (char === '"') {
        isInsideJsonString = true;
        out.push(char);
        i += 1;
      } else if (char === '-' || isDigit(char)) {
        i = appendJsonNumericToken(s, n, i, out);
      } else {
        out.push(char);
        i += 1;
      }
    }
  }

  return out.join('');
}

function reviveBigIntStrings(_, value) {
  if (typeof value !== 'string' || !value.startsWith(BIGINT_SENTINEL_PREFIX)) {
    return value;
  }
  try {
    return BigInt(value.slice(BIGINT_SENTINEL_PREFIX.length));
  } catch (error) {
    if (error instanceof Error) {
      return value;
    }
    throw error;
  }
}

/**
 * Parse a JSON string; integer literals outside the safe integer range become `BigInt`
 * (smaller integers stay `number`).
 * @param {string} rawJsonString
 * @returns {unknown}
 */
export function parseJsonPreservingUnsafeIntegers(rawJsonString) {
  const processed = rewriteUnsafeJsonIntegerLiterals(rawJsonString);
  return JSON.parse(processed, reviveBigIntStrings);
}

/**
 * Serialize for JSON request bodies: `BigInt` values become JSON *number* tokens.
 * @param {unknown} data
 * @returns {string|undefined}
 */
export function toJson(data) {
  if (data === undefined) {
    return undefined;
  }
  const json = JSON.stringify(data, (_, v) => (typeof v === 'bigint' ? `${BIGINT_SENTINEL_PREFIX}${v.toString()}` : v));
  const unwrapBigIntInJson = new RegExp(String.raw`"${BIGINT_SENTINEL_PREFIX}(-?\d+)"`, 'g');
  return json.replaceAll(unwrapBigIntInJson, '$1');
}
